import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Terminal, Mail, Linkedin, Github, FileText, Download, Send, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  return (
    <footer className="border-t border-border mt-auto bg-muted/30">
      {/* Main Footer Content */}
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          
          {/* Brand & About */}
          <div className="space-y-4">
            <Link
              to="/"
              className="flex items-center gap-2 font-extrabold text-xl hover:opacity-80 transition-opacity"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              <Terminal className="h-5 w-5" />
              <span>DevBlog</span>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Practical notes on Azure cloud solutions, DevOps practices, Automation, Infrastructure as Code, Backend development and Frontend engineering.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <a
                href="https://github.com/vivek-1295"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="GitHub"
                data-testid="footer-github"
              >
                <Github className="h-4 w-4" />
              </a>
              <a
                href="https://linkedin.com/in/vivek-harish-patel"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href="mailto:vivek.harish.patel@gmail.com"
                className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="Email"
                data-testid="footer-email"
              >
                <Mail className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm uppercase tracking-wider">Quick Links</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/?category=Azure" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
                  Azure Articles
                </Link>
              </li>
              <li>
                <Link to="/?category=DevOps" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
                  DevOps Articles
                </Link>
              </li>
              <li>
                <Link to="/?category=Backend" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
                  Backend Articles
                </Link>
              </li>
            </ul>
          </div>

          {/* Resume Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm uppercase tracking-wider">Resume</h3>
            <p className="text-sm text-muted-foreground">
              Azure Solution & DevOps Engineer with expertise in cloud infrastructure and automation.
            </p>
            <div className="flex flex-col gap-2">
              <a
                href="/resume/vivek-patel-resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="footer-view-resume"
              >
                <FileText className="h-4 w-4" />
                View Resume
                <ExternalLink className="h-3 w-3" />
              </a>
              <a
                href="/resume/vivek-patel-resume.pdf"
                download="Vivek-Patel-Resume.pdf"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="footer-download-resume"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </a>
            </div>
          </div>

          {/* Newsletter Subscription */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm uppercase tracking-wider">Stay Updated</h3>
            <p className="text-sm text-muted-foreground">
              Subscribe to get notified about new articles and updates.
            </p>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background"
                data-testid="footer-email-input"
                required
              />
              <Button 
                type="submit" 
                className="w-full"
                data-testid="footer-subscribe-btn"
              >
                {subscribed ? (
                  <>Subscribed!</>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Subscribe
                  </>
                )}
              </Button>
            </form>
            {subscribed && (
              <p className="text-sm text-green-600 dark:text-green-400 animate-in">
                Thanks for subscribing!
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="border-t border-border">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a 
                href="mailto:vivek.harish.patel@gmail.com" 
                className="hover:text-foreground transition-colors flex items-center gap-2"
                data-testid="footer-contact-email"
              >
                <Mail className="h-4 w-4" />
                vivek.harish.patel@gmail.com
              </a>
              <a 
                href="https://medium.com/@vivek1295" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-foreground transition-colors flex items-center gap-2"
                data-testid="footer-medium"
              >
                <ExternalLink className="h-4 w-4" />
                Medium
              </a>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/images/vp-engineer-logo.png" 
                  alt="VP Engineer" 
                  className="h-6 w-auto dark:invert"
                />
              </Link>
              <p className="text-sm text-muted-foreground text-center">
                &copy; {new Date().getFullYear()} DevBlog by Vivek Patel. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
